import { useRef, useSyncExternalStore, useCallback, useEffect, useMemo } from "react";
function dispatchStorageEvent(key, newValue) {
  window.dispatchEvent(new StorageEvent("storage", { key, newValue }));
}
const setLocalStorageItem = (key, value) => {
  var _a;
  const stringifiedValue = typeof value === "object" ? JSON.stringify(value) : value;
  (_a = window.localStorage) == null ? void 0 : _a.setItem(key, stringifiedValue);
  dispatchStorageEvent(key, stringifiedValue);
};
const removeLocalStorageItem = (key) => {
  var _a;
  (_a = window.localStorage) == null ? void 0 : _a.removeItem(key);
  dispatchStorageEvent(key, null);
};
const getLocalStorageItem = (key) => {
  var _a;
  return (_a = window.localStorage) == null ? void 0 : _a.getItem(key);
};
function createLocalStorageSubscribe(key) {
  return (callback) => {
    const handler = (e) => {
      if (!e.key || e.key === key) {
        callback(e);
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  };
}
const getLocalStorageServerSnapshot = () => {
  return null;
};
function useLocalStorage(key, initialValue) {
  const lastValue = useRef(null);
  const getSnapshot = () => {
    const value2 = getLocalStorageItem(key);
    if (value2 === lastValue.current) return lastValue.current;
    lastValue.current = value2;
    return value2;
  };
  const store = useSyncExternalStore(
    createLocalStorageSubscribe(key),
    getSnapshot,
    getLocalStorageServerSnapshot
  );
  const setState = useCallback(
    (v) => {
      try {
        let nextState = v;
        if (typeof v === "function") {
          try {
            nextState = v(JSON.parse(store || ""));
          } catch (e) {
            console.warn(e);
            nextState = v(store || "");
          }
        }
        if (nextState === void 0 || nextState === null) {
          removeLocalStorageItem(key);
        } else {
          setLocalStorageItem(key, nextState);
        }
      } catch (e) {
        console.warn(e);
      }
    },
    [key, store]
  );
  useEffect(() => {
    try {
      if (getLocalStorageItem(key) === null && typeof initialValue !== "undefined") {
        setLocalStorageItem(key, initialValue);
      }
    } catch (err) {
      console.warn(`useLocalStorage useEffect error: ${err}`);
    }
  }, [key, initialValue]);
  const value = useMemo(() => {
    try {
      let parsedStore = store ? JSON.parse(store) : initialValue;
      parsedStore = parsedStore !== void 0 && parsedStore !== null && typeof parsedStore !== "object" ? `${parsedStore}` : parsedStore;
      return parsedStore;
    } catch (err) {
      console.warn(`useLocalStorage parse error: ${err}`);
      return store || initialValue;
    }
  }, [store, initialValue]);
  try {
    return [value, setState];
  } catch (err) {
    console.error(`useLocalStorage error: ${err}`);
    return [initialValue, setState];
  }
}
export {
  useLocalStorage
};
